import SiteFrame from "@/components/SiteFrame";
import ReferralAcknowledgment from '@/components/ReferralAcknowledgment';
import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Seo from "@/components/Seo";
import CompanyHome from "@/pages/CompanyHome";
import ServicePage from "@/pages/ServicePage";
import Book from "@/pages/Book";
import Landing from "@/pages/Landing";
import Archive from "@/pages/Archive";
import GalleryHub from "@/pages/GalleryHub";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import ConversionSuccess from "@/pages/ConversionSuccess";
import CheckoutStart from "@/pages/CheckoutStart";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminCloudStudio from "@/pages/AdminCloudStudio";
import DropCards from "@/pages/DropCards";
import Tour from "@/pages/Tour";
import TourCity from "@/pages/TourCity";
import FundTour from "@/pages/FundTour";
import PoleHub from "@/pages/PoleHub";
import NaughtyList from "@/pages/NaughtyList";
import ServicesIndex from "@/pages/ServicesIndex";
import { InsightsHub, InsightArticle } from "@/pages/Insights";
import AboutFotosOnTheGeaux from "@/pages/AboutFotosOnTheGeaux";

function PoleAndPolaroidsPage() {
  return (
    <>
      <Seo
        title="Pole & Polaroids New Orleans | Fotos on the Geaux"
        description="Explore Pole & Polaroids, the New Orleans-area guided pole photography experience by Fotos on the Geaux, featuring movement, editorial portraits and instant photo keepsakes."
        path="/archive/pole-and-polaroids-sept-25-2026"
        imageAlt="Pole & Polaroids editorial pole photography experience by Fotos on the Geaux in the New Orleans area"
      />
      <a
        href="https://geauxfotos.com/"
        aria-label="Visit the Fotos on the Geaux home page"
        className="fixed top-[72px] right-4 z-[60] rounded-full border border-white/15 bg-[#090909]/85 px-4 py-2 font-accent text-xs tracking-[0.18em] text-[#F5F0E8]/80 backdrop-blur-xl transition hover:border-[#39E75F] hover:text-[#39E75F] md:top-[76px] md:right-6"
      >
        FOTOS HOME
      </a>
      <Landing />
    </>
  );
}

function NoIndex({ children, path, title }) {
  return (
    <>
      <Seo title={title} description="Fotos on the Geaux" path={path} robots="noindex,nofollow" />
      {children}
    </>
  );
}

function App() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) localStorage.setItem("pp_ref", ref);
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ReferralAcknowledgment />
        <SiteFrame><Routes>
          <Route path="/" element={<CompanyHome />} />
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/pole-and-polaroids" element={<PoleHub />} />
          <Route path="/pole-and-polaroids/naughty-list" element={<NaughtyList />} />
          <Route path="/archive/pole-and-polaroids-sept-25-2026" element={<NoIndex path="/archive/pole-and-polaroids-sept-25-2026" title="Pole & Polaroids Sept. 25, 2026 | Archive"><PoleAndPolaroidsPage /></NoIndex>} />
          <Route path="/book" element={<Book />} />
          <Route path="/services" element={<ServicesIndex />} />
          <Route path="/about-fotos-on-the-geaux" element={<AboutFotosOnTheGeaux />} />
          <Route path="/insights" element={<InsightsHub />} />
          <Route path="/insights/:slug" element={<InsightArticle />} />

          <Route
            path="/services/event-photography-new-orleans"
            element={<ServicePage
              title="New Orleans Event Photographer | Fotos on the Geaux"
              description="Professional New Orleans event photography for birthdays, nightlife, private celebrations, brand activations and community events, with a candid editorial approach."
              path="/services/event-photography-new-orleans"
              eyebrow="NEW ORLEANS EVENT PHOTOGRAPHER"
              h1="Event photography with the energy left in."
              intro="Fotos on the Geaux photographs events with a candid, editorial and flash-forward approach designed to preserve the atmosphere as much as the people in it."
              serviceType="Event Photography"
              bullets={[
                "Candid and editorial event coverage for celebrations, nightlife, private events and activations.",
                "Photography designed around natural guest movement instead of stopping the event for every frame.",
                "Coverage that can pair with on-site instant prints and physical photo keepsakes.",
                "Local service across New Orleans, Metairie and the Greater New Orleans area.",
              ]}
            />}
          />

          <Route
            path="/services/foto-booth-new-orleans"
            element={<ServicePage
              title="New Orleans Photo Booth Rental | Fotos on the Geaux"
              description="Book a hosted New Orleans Foto Booth for parties, weddings and brand activations, with guided posing, digital delivery and optional instant prints."
              path="/services/foto-booth-new-orleans"
              eyebrow="NEW ORLEANS FOTO BOOTH"
              h1="A foto booth that feels like part of the party."
              intro="The Fotos on the Geaux Foto Booth is a hosted, guest-first photo experience built for events that deserve more than a generic kiosk. Guests step in, get directed into flattering portraits and leave with digital memories, with instant-print keepsakes available as an upgrade."
              serviceType="Foto Booth Experience"
              bullets={[
                "Hosted photo booth experience for parties, weddings, corporate events and brand activations.",
                "Guided posing and flattering event portraits instead of unattended snapshots.",
                "Digital photo delivery with optional on-site instant prints and keepsakes.",
                "Custom event and brand options available for a more tailored guest experience.",
              ]}
            />}
          />

          <Route
            path="/services/real-estate-media-new-orleans"
            element={<ServicePage
              title="New Orleans Real Estate Photography | Fotos on the Geaux"
              description="Real estate photography and listing media for New Orleans agents, property managers, investors and short-term rental owners."
              path="/services/real-estate-media-new-orleans"
              eyebrow="REAL ESTATE MEDIA"
              h1="Property visuals built to make the listing work harder."
              intro="Fotos on the Geaux creates clean, polished property photography and short-form listing content for agents, property managers, investors and owners who need stronger visuals across MLS, websites and social media."
              serviceType="Real Estate Media"
              bullets={[
                "Interior and exterior property photography for listings, rentals and marketing.",
                "Vertical social clips and short-form property video available with select packages.",
                "Fast-turnaround delivery options for active listings and time-sensitive launches.",
                "Aerial and drone media is planned as a future service and is not currently bookable.",
              ]}
            />}
          />

          <Route
            path="/services/restaurant-food-photography-new-orleans"
            element={<ServicePage
              title="New Orleans Food & Restaurant Photography | Fotos on the Geaux"
              description="Food, menu, restaurant and hospitality photography plus social content for New Orleans restaurants, bars and culinary brands."
              path="/services/restaurant-food-photography-new-orleans"
              eyebrow="FOOD + RESTAURANT CONTENT"
              h1="Make the menu look as good as it tastes."
              intro="From hero menu photography to interiors, staff, atmosphere and short-form social content, Fotos on the Geaux helps restaurants and bars build a visual library that can work across menus, websites, Google and social."
              serviceType="Restaurant and Food Photography"
              bullets={[
                "Menu, plated food, cocktail and product photography.",
                "Restaurant interior, atmosphere, staff and lifestyle coverage.",
                "Vertical reels and short-form social assets available as package add-ons.",
                "Monthly content options for restaurants that need a steady flow of fresh visuals.",
              ]}
            />}
          />

          <Route
            path="/services/hospitality-airbnb-media-new-orleans"
            element={<ServicePage
              title="New Orleans Airbnb & Hospitality Photography | Fotos on the Geaux"
              description="Photography and video content for New Orleans Airbnb hosts, short-term rentals, boutique hotels and hospitality properties."
              path="/services/hospitality-airbnb-media-new-orleans"
              eyebrow="AIRBNB + HOSPITALITY"
              h1="Show people why they should stay."
              intro="Fotos on the Geaux creates property, detail and lifestyle imagery for short-term rentals, boutique stays and hospitality brands that need stronger booking visuals across Airbnb, Vrbo, websites and social platforms."
              serviceType="Airbnb and Hospitality Media"
              bullets={[
                "Interior, exterior, amenity and detail photography.",
                "Lifestyle content that helps guests understand the experience, not just the room.",
                "Short vertical video for websites, reels and booking campaigns.",
                "Neighborhood and destination content available for custom hospitality packages.",
              ]}
            />}
          />

          <Route
            path="/services/business-content-new-orleans"
            element={<ServicePage
              title="New Orleans Business Content | Fotos on the Geaux"
              description="Brand photography, short-form video and monthly content packages for New Orleans businesses, creators and entrepreneurs."
              path="/services/business-content-new-orleans"
              eyebrow="BUSINESS CONTENT"
              h1="A content library your business can actually use."
              intro="Fotos on the Geaux builds practical photo and video content for local businesses that need stronger websites, ads, social media and Google Business Profile visuals without coordinating a different creator for every channel."
              serviceType="Business Content Production"
              bullets={[
                "Brand photography for websites, ads, social media and Google Business Profiles.",
                "Short-form vertical video and campaign-ready social assets.",
                "Product, team, workspace and customer-experience content.",
                "Recurring monthly content retainers for businesses that need consistent production.",
              ]}
            />}
          />

          <Route
            path="/services/event-video-new-orleans"
            element={<ServicePage
              title="New Orleans Event Recap Video | Fotos on the Geaux"
              description="Cinematic event recap video and vertical social content for New Orleans celebrations, brands, nightlife and community events."
              path="/services/event-video-new-orleans"
              eyebrow="EVENT VIDEO"
              h1="Turn the event into content that keeps moving."
              intro="Fotos on the Geaux creates cinematic recap edits and social-first video from events so the energy continues after the room clears."
              serviceType="Event Recap Video"
              bullets={[
                "Cinematic highlight and recap edits for events, activations and nightlife.",
                "Vertical reels and social-first cutdowns for Instagram, TikTok and Facebook.",
                "Photo and video coverage can be bundled into one production plan.",
                "Fast-turnaround options are available for campaigns that need to post quickly.",
              ]}
            />}
          />

          <Route
            path="/services/instant-photo-printing-new-orleans"
            element={<ServicePage
              title="Instant Photo Printing New Orleans | Fotos on the Geaux"
              description="On-site instant photo printing in New Orleans from Fotos on the Geaux. Give guests physical photo keepsakes created during your event."
              path="/services/instant-photo-printing-new-orleans"
              eyebrow="ON-SITE INSTANT PHOTO PRINTING"
              h1="Shoot it. Print it. Take it home."
              intro="Fotos on the Geaux brings physical photography back into the room with on-site instant printing that turns event photos into take-home keepsakes before the night is over."
              serviceType="On-site Instant Photo Printing"
              bullets={[
                "Physical photo keepsakes produced during the event.",
                "A photography-first alternative to a stationary photo booth experience.",
                "Instant-print options designed for parties, nightlife, weddings, brand events and private celebrations.",
                "A guest experience that connects the digital moment to something tangible.",
              ]}
            />}
          />

          <Route
            path="/services/open-bar-for-photos"
            element={<ServicePage
              title="Open Bar for Photos | Fotos on the Geaux"
              description="Open Bar for Photos by Fotos on the Geaux is a hosted event photography experience with continuous coverage and take-home prints for guests in New Orleans."
              path="/services/open-bar-for-photos"
              eyebrow="OPEN BAR FOR PHOTOS"
              h1="Photos flowing all event long."
              intro="Open Bar for Photos is Fotos on the Geaux's hosted photography experience: continuous event coverage paired with physical guest keepsakes throughout the celebration."
              serviceType="Open Bar for Photos Event Photography"
              bullets={[
                "Continuous photography designed to move throughout the event.",
                "Take-home photo prints for guests instead of a gallery-only experience.",
                "A strong fit for private celebrations, nightlife, hospitality and brand activations.",
                "Designed to feel integrated into the room rather than isolated in a booth setup.",
              ]}
            />}
          />

          <Route
            path="/new-orleans-event-photography"
            element={<ServicePage
              title="New Orleans Photography, Video & Event Media | Fotos on the Geaux"
              description="Fotos on the Geaux creates photography, video, instant prints, business content and event experiences for New Orleans brands, properties and people."
              path="/new-orleans-event-photography"
              eyebrow="NEW ORLEANS VISUAL MEDIA"
              h1="Made for New Orleans businesses, properties and events."
              intro="Fotos on the Geaux combines photography, video, physical prints and guest-focused activations for businesses, listings, hospitality, food brands, events and creative campaigns."
              serviceType="New Orleans Visual Media"
              bullets={[
                "Event photography and cinematic recap video.",
                "Business, restaurant, real estate and hospitality content.",
                "On-site instant printing, Foto Booth experiences and physical keepsakes.",
                "Aerial and drone media is planned for a future launch after additional flight practice and production development.",
              ]}
            />}
          />

          <Route path="/tour" element={<Tour />} />
          <Route path="/tour/houston" element={<TourCity citySlug="houston" />} />
          <Route path="/tour/chicago" element={<TourCity citySlug="chicago" />} />
          <Route path="/tour/:city" element={<Tour />} />
          <Route path="/fun" element={<FundTour />} />
          <Route path="/fund" element={<Navigate to="/fun" replace />} />
          <Route path="/gallery" element={<GalleryHub />} />
          <Route path="/archive" element={<NoIndex path="/archive" title="Archive | Fotos on the Geaux"><Archive /></NoIndex>} />
          <Route path="/payment/success" element={<NoIndex path="/payment/success" title="Payment Complete | Fotos on the Geaux"><PaymentSuccess /></NoIndex>} />
          <Route path="/checkout/start" element={<NoIndex path="/checkout/start" title="Secure Checkout | Fotos on the Geaux"><CheckoutStart /></NoIndex>} />
          <Route path="/book/success" element={<NoIndex path="/book/success" title="Booking Request Received | Fotos on the Geaux"><ConversionSuccess type="booking" /></NoIndex>} />
          <Route path="/waitlist/success" element={<NoIndex path="/waitlist/success" title="Waitlist Confirmed | Fotos on the Geaux"><ConversionSuccess type="waitlist" /></NoIndex>} />
          <Route path="/partner/success" element={<NoIndex path="/partner/success" title="Partner Inquiry Received | Fotos on the Geaux"><ConversionSuccess type="partner" /></NoIndex>} />
          <Route path="/payment/cancel" element={<NoIndex path="/payment/cancel" title="Payment Cancelled | Fotos on the Geaux"><PaymentCancel /></NoIndex>} />
          <Route path="/admin/login" element={<NoIndex path="/admin/login" title="Admin | Fotos on the Geaux"><AdminLogin /></NoIndex>} />
          <Route path="/admin" element={<NoIndex path="/admin" title="Admin | Fotos on the Geaux"><AdminDashboard /></NoIndex>} />
          <Route path="/admin/cloud-studio" element={<NoIndex path="/admin/cloud-studio" title="Cloud Studio | Fotos on the Geaux"><AdminCloudStudio /></NoIndex>} />
          <Route path="/cards" element={<NoIndex path="/cards" title="Cards | Fotos on the Geaux"><DropCards /></NoIndex>} />
        </Routes></SiteFrame>
      </BrowserRouter>
      <Toaster position="top-center" theme="dark" />
    </div>
  );
}

export default App;


